import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/router';

// NOTE: Shared states
let overflowCount = 0;
const activeSourceIds: Set<string> = new Set();

const useBodyOverflow = (
  condition: boolean,
  sourceId: string,
  isFixedPosition = false,
  disableOnRouteChange = false,
): [boolean] => {
  const overflowClass = '--no-overflow';
  const router = useRouter();
  const [currentBodyOverflow, setCurrentBodyOverflow] = useState(false);

  const enableOverflow = useCallback(() => {
    if (!activeSourceIds.has(sourceId)) {
      overflowCount += 1;
      activeSourceIds.add(sourceId);
      if (overflowCount === 1) {
        window.document?.body.classList.add(overflowClass);
        if (isFixedPosition) {
          const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
          if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
          }
        }
      }
      setCurrentBodyOverflow(true);
    }
  }, [sourceId, isFixedPosition]);

  const disableOverflow = useCallback(() => {
    if (activeSourceIds.has(sourceId)) {
      overflowCount = Math.max(0, overflowCount - 1);
      activeSourceIds.delete(sourceId);
      if (overflowCount === 0) {
        window.document?.body.classList.remove(overflowClass);
        document.body.style.paddingRight = '';
      }
      setCurrentBodyOverflow(false);
    }
  }, [sourceId]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (disableOnRouteChange) {
        disableOverflow();
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      if (disableOnRouteChange) {
        disableOverflow();
      }
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, disableOnRouteChange, disableOverflow]);

  useEffect(() => {
    if (condition) {
      enableOverflow();
    } else {
      disableOverflow();
    }
  }, [condition, enableOverflow, disableOverflow]);

  return [currentBodyOverflow];
};

export default useBodyOverflow;
